import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AudioPlayer from '../components/AudioPlayer';

// Recordings library — standalone page (extracted from Settings for supervisors,
// Karim 2026-10-14: external-team supervisors get Monitor + Recordings ONLY).
// Backend scopes the list + the audio stream to the supervisor's own agents.
export default function Recordings() {
  const [recordings, setRecordings] = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [filters, setFilters]       = useState({ search: '', date_from: '', date_to: '', agent_id: '' });
  const [agents, setAgents]         = useState([]);

  useEffect(() => {
    api.get('/agents').then(d => setAgents(d || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filters.search)    params.search    = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to)   params.date_to   = filters.date_to;
      if (filters.agent_id)  params.agent_id  = filters.agent_id;
      const data = await api.get('/calls/recordings/list', { params });
      setRecordings(data.recordings || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const fmtDur = s => s ? `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}` : '—';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Recordings</h1>
        <p className="page-subtitle">Call recordings of your team</p>
      </div>

      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16, alignItems:'flex-end' }}>
        <div>
          <div className="form-label" style={{ marginBottom:4 }}>Search (name / phone / agent)</div>
          <input className="input" style={{ width:260 }} placeholder="Search recordings..." value={filters.search} onChange={e => setF('search', e.target.value)} />
        </div>
        <div>
          <div className="form-label" style={{ marginBottom:4 }}>Agent</div>
          <select className="select" style={{ width:180 }} value={filters.agent_id} onChange={e => setF('agent_id', e.target.value)}>
            <option value="">All Agents</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name} (EXT {a.extension})</option>)}
          </select>
        </div>
        <div>
          <div className="form-label" style={{ marginBottom:4 }}>From</div>
          <input className="input" type="date" style={{ width:150 }} value={filters.date_from} onChange={e => setF('date_from', e.target.value)} />
        </div>
        <div>
          <div className="form-label" style={{ marginBottom:4 }}>To</div>
          <input className="input" type="date" style={{ width:150 }} value={filters.date_to} onChange={e => setF('date_to', e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={load}>Search</button>
        {(filters.search || filters.agent_id || filters.date_from) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilters({ search:'', date_from:'', date_to:'', agent_id:'' }); setPage(1); }}>Clear</button>
        )}
      </div>

      <div style={{ marginBottom:12, fontSize:13, color:'var(--text-secondary)' }}>
        {total.toLocaleString()} recording{total !== 1 ? 's' : ''} found
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-secondary)' }}>Loading recordings...</div>
        ) : recordings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎙</div>
            <div className="empty-text">No recordings found</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Phone</th>
                <th>Lead Name</th>
                <th>Agent</th>
                <th>Duration</th>
                <th style={{ minWidth:260 }}>Recording</th>
              </tr>
            </thead>
            <tbody>
              {recordings.map(r => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize:11, whiteSpace:'nowrap' }}>
                    {new Date(r.called_at).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </td>
                  <td><span className="mono" style={{ color:'var(--accent)' }}>{r.phone || '—'}</span></td>
                  <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td>
                    <div style={{ fontWeight:600 }}>{r.agent_name || '—'}</div>
                    <div style={{ fontSize:11, color:'var(--text-secondary)', fontFamily:'var(--font-mono)' }}>EXT {r.agent_extension || '—'}</div>
                  </td>
                  <td className="mono">{fmtDur(r.duration)}</td>
                  <td style={{ minWidth:260 }}>
                    <AudioPlayer callId={r.id} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 50 && (
        <div className="pagination" style={{ marginTop:12 }}>
          <button className="btn btn-secondary btn-sm" disabled={page===1} onClick={() => setPage(p => p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/50)} · {total.toLocaleString()} total</span>
          <button className="btn btn-secondary btn-sm" disabled={page*50>=total} onClick={() => setPage(p => p+1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
